import { useState, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { FileText, Download, Eye, ShieldCheck, AlertCircle } from 'lucide-react';
import { toast } from 'sonner';
import api from '@/lib/api';

/**
 * Tarjeta que muestra el informe oficial NSYS adjunto a la OT.
 *
 * Estados:
 *  - Si `nsys_diagnostics_certificate.pdf_local_url` existe: muestra preview
 *    embebido (iframe) + botones Ver / Descargar.
 *  - Si `nsys_disponible` pero todavía no se descargó el PDF: muestra botón
 *    "Descargar ahora" que llama a `/api/nsys/diagnostics-certificate`.
 *  - Si nada: no se renderiza.
 *
 * @param {object} orden       - documento de la OT
 * @param {Function} onRefresh - callback para refrescar la OT tras descargar
 */
export default function NsysCertificateCard({ orden, onRefresh }) {
  const [loading, setLoading] = useState(false);
  const [blobUrl, setBlobUrl] = useState(null);

  const cert = orden?.nsys_diagnostics_certificate || {};
  const pdfLocalUrl = cert.pdf_local_url;
  const fetchedAt = cert.fetched_at;
  const nsysDisponible = orden?.nsys_disponible;

  // Carga el PDF como blob (con auth) para mostrarlo en iframe sin exponer el token.
  // `fetchedAt` se incluye como dependencia para que tras un "Refrescar desde NSYS"
  // se vuelva a descargar el PDF actualizado (mismo path local, contenido distinto).
  useEffect(() => {
    let cancelled = false;
    let createdUrl = null;
    if (pdfLocalUrl) {
      const token = localStorage.getItem('token');
      // Cache-buster con `fetchedAt` para evitar 304 del navegador
      const bust = fetchedAt ? `?t=${encodeURIComponent(fetchedAt)}` : '';
      fetch(`${process.env.REACT_APP_BACKEND_URL}${pdfLocalUrl}${bust}`, {
        headers: { Authorization: `Bearer ${token}` },
      })
        .then((r) => r.blob())
        .then((b) => {
          if (cancelled) return;
          createdUrl = URL.createObjectURL(b);
          setBlobUrl(createdUrl);
        })
        .catch(() => {});
    }
    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [pdfLocalUrl, fetchedAt]);

  if (!pdfLocalUrl && !nsysDisponible) return null;

  const handleDescargarAhora = async (force = false) => {
    setLoading(true);
    try {
      const url = force
        ? '/nsys/diagnostics-certificate?force=true'
        : '/nsys/diagnostics-certificate';
      const { data } = await api.post(url, {
        orden_id: orden.id,
        imei: orden.dispositivo?.imei,
        serial_number: orden.dispositivo?.serial,
      });
      if (data?.pdf_local_url) {
        toast.success(
          force
            ? `Informe NSYS re-descargado (${Math.round(data.pdf_size_bytes / 1024)} KB)`
            : `Informe NSYS adjuntado a la OT (${Math.round(data.pdf_size_bytes / 1024)} KB)`
        );
        onRefresh?.();
      } else {
        toast.warning('Informe NSYS no disponible aún en tu cuenta');
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Error al descargar el informe NSYS');
    } finally {
      setLoading(false);
    }
  };

  /**
   * "Refrescar desde NSYS" — fuerza la consulta completa contra el servidor
   * de NSYS, no solo el PDF cacheado. Esto trae el diagnóstico MÁS RECIENTE
   * (típicamente el de SALIDA si el técnico lo lanzó después del de ENTRADA)
   * y lo guarda como `nsys_data` actual, manteniendo `nsys_data_entrada` y
   * `nsys_data_salida` como histórico.
   */
  const handleRefrescarTotal = async () => {
    setLoading(true);
    const tid = toast.loading('Refrescando datos NSYS (último diagnóstico disponible)…');
    try {
      // 1) Re-consultar device-info → actualiza KPIs, tests, FMIP, etc.
      //    Usamos momento='general' para refrescar `nsys_data` (snapshot actual)
      //    sin sobrescribir el histórico `nsys_data_entrada` / `nsys_data_salida`.
      //    NSYS siempre devuelve el último diagnóstico realizado, por lo que esto
      //    trae automáticamente el de SALIDA si el técnico ya lo lanzó.
      await api.post('/nsys/device-info', {
        orden_id: orden.id,
        imei: orden.dispositivo?.imei,
        serial_number: orden.dispositivo?.serial,
        momento: 'general',
      });
      // 2) Re-descargar certificado con force=true para invalidar el PDF cacheado
      const { data } = await api.post('/nsys/diagnostics-certificate?force=true', {
        orden_id: orden.id,
        imei: orden.dispositivo?.imei,
        serial_number: orden.dispositivo?.serial,
      });
      toast.dismiss(tid);
      if (data?.pdf_local_url) {
        toast.success(`Datos NSYS y certificado actualizados (${Math.round(data.pdf_size_bytes / 1024)} KB)`);
      } else {
        toast.success('Datos NSYS actualizados (certificado no disponible)');
      }
      // Limpiar el blob viejo para forzar recarga del iframe con el nuevo PDF
      setBlobUrl(null);
      onRefresh?.();
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || 'Error refrescando NSYS');
    } finally {
      setLoading(false);
    }
  };

  const handleDownload = () => {
    if (!blobUrl) return;
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = `${orden.numero_orden || orden.id}_NSYS_certificado.pdf`;
    a.click();
  };

  return (
    <Card className="border-teal-200 bg-teal-50/30" data-testid="nsys-cert-card">
      <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-3">
        <div>
          <CardTitle className="flex items-center gap-2 text-base">
            <ShieldCheck className="w-5 h-5 text-teal-600" />
            Informe oficial NSYS
            {pdfLocalUrl && (
              <Badge variant="outline" className="bg-teal-100 text-teal-700 border-teal-300 text-[10px]">
                ADJUNTO
              </Badge>
            )}
          </CardTitle>
          {cert.fetched_at && (
            <p className="text-xs text-muted-foreground mt-1">
              Adjuntado el {new Date(cert.fetched_at).toLocaleString('es-ES')}
              {cert.pdf_size_bytes && ` · ${Math.round(cert.pdf_size_bytes / 1024)} KB`}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {pdfLocalUrl && blobUrl && (
            <>
              <Button variant="outline" size="sm" onClick={() => window.open(blobUrl, '_blank')} data-testid="btn-ver-nsys">
                <Eye className="w-4 h-4 mr-1" /> Ver
              </Button>
              <Button variant="outline" size="sm" onClick={handleDownload} data-testid="btn-descargar-nsys">
                <Download className="w-4 h-4 mr-1" /> Descargar
              </Button>
              <Button
                variant="ghost"
                size="sm"
                onClick={handleRefrescarTotal}
                disabled={loading}
                title="Vuelve a consultar NSYS para traer el último diagnóstico (ENTRADA o SALIDA) y refrescar el PDF"
                data-testid="btn-redescargar-nsys"
              >
                <FileText className="w-4 h-4 mr-1" />
                {loading ? 'Refrescando…' : 'Refrescar desde NSYS'}
              </Button>
            </>
          )}
          {!pdfLocalUrl && nsysDisponible && (
            <Button size="sm" onClick={() => handleDescargarAhora(false)} disabled={loading} data-testid="btn-adjuntar-nsys">
              <FileText className="w-4 h-4 mr-1" />
              {loading ? 'Descargando…' : 'Adjuntar a la OT'}
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        {pdfLocalUrl && blobUrl ? (
          <div className="rounded-lg border bg-white overflow-hidden" style={{ height: '600px' }}>
            <iframe
              src={blobUrl}
              title="Informe oficial NSYS"
              className="w-full h-full border-0"
              data-testid="iframe-nsys"
            />
          </div>
        ) : pdfLocalUrl && !blobUrl ? (
          <div className="flex items-center justify-center py-8 text-sm text-muted-foreground">
            Cargando informe…
          </div>
        ) : nsysDisponible ? (
          <div className="flex items-start gap-2 text-sm text-amber-700 bg-amber-50 p-3 rounded-lg border border-amber-200">
            <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
            <div>
              <p className="font-medium">Diagnóstico disponible en NSYS</p>
              <p className="text-xs mt-0.5">
                Pulsa "Adjuntar a la OT" para descargar el informe oficial y mantenerlo
                disponible para consultas futuras y garantías. Solo se hace una vez.
              </p>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
}
